import { SyntheticEvent, useEffect, useRef, useState } from "react";
import { AttributeTextStringModel } from "../../api/glossaryAttributeTextStringApi";
import { StandardTextStringModel } from "../../api/glossaryStandardTextStringApi";
import { formErrors, Result, ValidationProblem } from "../../api/backendApi";
import {
  Alert,
  Button,
  Form,
  InputGroup,
  Modal,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { ArrowRepeat, Pencil, Search, Shuffle } from "react-bootstrap-icons";

interface GlossaryTextModalProps {
  glossaryText: StandardTextStringModel | AttributeTextStringModel;
  searchOrders: (number | undefined)[];
  isCreate: boolean;
  isCopy?: boolean;
  onSubmit: (
    glossaryText: StandardTextStringModel | AttributeTextStringModel,
  ) => Promise<Result<any>>;
  onChange: (
    glossaryText: StandardTextStringModel | AttributeTextStringModel,
  ) => void;
  onCancel: () => void;
  modalText: string;
  type: "standard" | "attribute";
}

function GlossaryTextModal({
  glossaryText: initialGlossaryText,
  searchOrders,
  isCreate,
  isCopy = false,
  onSubmit,
  onChange,
  onCancel,
  modalText,
  type,
}: GlossaryTextModalProps) {
  const [glossaryText, setGlossaryText] = useState<
    StandardTextStringModel | AttributeTextStringModel
  >(initialGlossaryText);
  const [show, setShow] = useState<boolean>(true);
  const [errors, setErrors] = useState<formErrors>({});

  const [search, setSearch] = useState<string>("");
  const [searchResult, setSearchResult] = useState<number>(0);
  const [replace, setReplace] = useState<string>("");

  const formEntries = [
    {
      title: "Search Order",
      value: "searchOrder",
    },
    {
      title: "English",
      value: "english",
    },
    {
      title: "Dutch",
      value: "dutch",
    },
    {
      title: "German",
      value: "german",
    },
    {
      title: "French",
      value: "french",
    },
    {
      title: "Spanish",
      value: "spanish",
    },
    {
      title: "Portuguese",
      value: "portuguese",
    },
    {
      title: "Italian",
      value: "italian",
    },
    {
      title: "Polish",
      value: "polish",
    },
    {
      title: "Czech",
      value: "czech",
    },
    {
      title: "Swedish",
      value: "swedish",
    },
    {
      title: "Danish",
      value: "danish",
    },
    {
      title: "Norwegian",
      value: "norwegian",
    },
    {
      title: "Finnish",
      value: "finnish",
    },
    {
      title: "Hungarian",
      value: "hungarian",
    },
    {
      title: "Croatian",
      value: "croatian",
    },
    {
      title: "Hebrew",
      value: "hebrew",
    },
    {
      title: "Slovene",
      value: "slovene",
    },
    {
      title: "Russian",
      value: "russian",
    },
    {
      title: "Arabic",
      value: "arabic",
    },
    {
      title: "Ukrainian",
      value: "ukrainian",
    },
  ];

  const handleClose = () => {
    setShow(false);
    onCancel();
    setGlossaryText(initialGlossaryText);
  };

  useEffect(() => {
    async function loadGlossaryTextSearchOrder() {
      if (searchOrders.length !== 0) {
        const maxSearchOrder =
          Math.max(...searchOrders.map((x) => (x !== undefined ? x : 0))) + 1;

        if (isCreate) {
          var change = { searchOrder: maxSearchOrder };

          const modified: StandardTextStringModel | AttributeTextStringModel = {
            ...glossaryText,
            ...change,
          };

          setGlossaryText(modified);
        }
      }
    }

    loadGlossaryTextSearchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //needs to be moved up to the parent component
  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();

    let call = onSubmit(glossaryText);

    call.then((result) => {
      if (result.ok) {
        if (result.value !== undefined && isCreate) {
          glossaryText.stringId = result.value;
        }
        onChange(glossaryText);
        handleClose();
      } else {
        let validation = result.problem as ValidationProblem;
        if (validation.validation) {
          if (glossaryText.searchOrder !== undefined) {
            if (
              searchOrders.find((s) => s === glossaryText.searchOrder) &&
              glossaryText.searchOrder !== initialGlossaryText.searchOrder
            ) {
              validation.validation.searchOrder = [];
              validation.validation.searchOrder.push(
                `Search order ${glossaryText.searchOrder} is already used on a different Title text.`,
              );
            }
          }
          setErrors(validation.validation);
        } else {
          let errors: formErrors = {};
          errors[""] = [`${result.problem.title} [${result.problem.instance}]`];
          setErrors(errors);
        }
      }
    });
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    var change = null;

    if (name === "searchOrder") {
      if (value !== "") {
        change = { [name]: parseInt(value) };
      } else {
        change = { [name]: undefined };
      }

      if (
        searchOrders.find((s) => s === parseInt(value)) &&
        parseInt(value) !== initialGlossaryText.searchOrder
      ) {
        errors.searchOrder = [];
        errors.searchOrder.push(
          `Search order ${value} is already used on a different Title text.`,
        );
      } else {
        delete errors["searchOrder"];
      }
    } else {
      const lines: string[] = value.toString().split("<NL>");

      if (lines.findIndex((t) => overflows(t)) >= 0) {
        errors[name] = ["Text will overflow in the rescue sheet."];
      } else {
        delete errors[name];
      }

      change = { [name]: value };
    }

    const modified: StandardTextStringModel | AttributeTextStringModel = {
      ...glossaryText,
      ...change,
    };

    setGlossaryText(modified);
  };

  const handleSearchAndReplace = () => {
    var glossaryTextString = { ...glossaryText };

    if (searchResult !== 0) {
      Object.entries(glossaryTextString).forEach(([key, value]) => {
        if (typeof value === "string") {
          glossaryTextString[key] = value
            ?.toString()
            .replaceAll(search, replace);
        }
      });

      const modified: StandardTextStringModel | AttributeTextStringModel = {
        ...glossaryText,
        ...glossaryTextString,
      };

      setGlossaryText(modified);
      handleSearch(search, modified);
    }
  };

  const handleSearch = (search: string, text = glossaryText) => {
    setSearch(search);

    var count = 0;
    if (search !== "") {
      Object.entries(text).forEach(([key, value]) => {
        if (typeof value === "string") {
          if (text[key]?.toString().includes(search)) {
            var escapedSearch = search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            var indexes = text[key]
              ?.toString()
              .match(new RegExp(escapedSearch, "g"))?.length;
            if (indexes) {
              count = count + indexes;
            }
          }
        }
      });
    }
    setSearchResult(count);
  };

  const svgText = useRef<SVGTextElement>(null);

  const overflows = (value: string): boolean => {
    if (!svgText || !svgText.current) return false;

    svgText.current.innerHTML = value.replace(/</g, "&lt;");

    const result = svgText.current.getComputedTextLength() > 278;

    return result;
  };

  return (
    <>
      <Modal dialogClassName="modal-80w" show={show} onHide={handleClose}>
        <Modal.Header closeButton style={{ display: "row" }}>
          <Modal.Title style={{ minWidth: "max-content" }}>
            {modalText}
          </Modal.Title>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              width: "fit-content",
              marginLeft: "auto",
            }}
          >
            <InputGroup size="sm" style={{ display: "flex", width: "40%" }}>
              <InputGroup.Text
                id="inputGroup-sizing-sm"
                style={{
                  fontWeight: "400",
                  display: "flex",
                  marginTop: "auto",
                  marginBottom: "auto",
                  marginLeft: "5px",
                }}
              >
                <Search width={10} height={10} style={{ marginRight: "5px" }} />
                Search ({searchResult})
              </InputGroup.Text>
              <Form.Control
                name="search"
                onChange={(e) => {
                  handleSearch(e.target.value);
                }}
                aria-label="Small"
                aria-describedby="inputGroup-sizing-sm"
                isInvalid={search !== "" && searchResult === 0}
              />
              <Form.Control.Feedback type="invalid" tooltip={true}>
                '{search}' not found
              </Form.Control.Feedback>
            </InputGroup>
            <InputGroup size="sm" style={{ display: "flex", width: "40%" }}>
              <InputGroup.Text
                id="inputGroup-sizing-sm"
                style={{
                  fontWeight: "400",
                  display: "flex",
                  marginTop: "auto",
                  marginBottom: "auto",
                  marginLeft: "5px",
                }}
              >
                <ArrowRepeat
                  width={10}
                  height={10}
                  style={{ marginRight: "5px" }}
                />
                Replace
              </InputGroup.Text>
              <Form.Control
                aria-label="Small"
                aria-describedby="inputGroup-sizing-sm"
                onChange={(e) => {
                  setReplace(e.target.value);
                }}
              />
            </InputGroup>
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id="tooltip-bottom">Replace all</Tooltip>}
              delay={{ show: 250, hide: 150 }}
            >
              <Button
                onClick={handleSearchAndReplace}
                disabled={
                  !(search && replace) || searchResult === 0 ? true : false
                }
                size="sm"
                style={{ marginLeft: "5px" }}
                variant="primary"
                type="submit"
              >
                <Shuffle />
              </Button>
            </OverlayTrigger>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form
            noValidate
            style={{ maxHeight: "1000px", overflowY: "scroll" }}
            onSubmit={handleSubmit}
            id="standardTextStringForm"
          >
            {errors[""] && (
              <div style={{ marginTop: "1rem" }}>
                {errors[""].map((errorMessage, index) => (
                  <Alert key={errorMessage} variant="danger">
                    <p key={index}>{errorMessage}</p>
                  </Alert>
                ))}
              </div>
            )}
            {formEntries.map((f, index) => (
              <Form.Group key={index}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginTop: "10px",
                    maxHeight: "fit-content",
                  }}
                >
                  <Form.Label style={{ fontWeight: "500", minWidth: "100px" }}>
                    {f.title}
                  </Form.Label>
                  <div style={{ width: "100%", display: "flex" }}>
                    <InputGroup>
                      <Form.Control
                        name={f.value}
                        type={f.value === "searchOrder" ? "number" : "text"}
                        style={
                          f.value === "searchOrder"
                            ? { maxWidth: "max-content", marginRight: "auto" }
                            : {}
                        }
                        value={(glossaryText as any)[f.value] ?? ""}
                        onChange={handleChange}
                        size="sm"
                        isInvalid={
                          (errors as formErrors)[f.value]?.length ===
                            undefined ||
                          (errors as formErrors)[f.value]?.length === 0
                            ? false
                            : true
                        }
                      />
                      {isCopy && f.value !== "searchOrder" && (
                        <InputGroup.Text>
                          <Pencil
                            style={{
                              color:
                                initialGlossaryText[f.value] ===
                                glossaryText[f.value]
                                  ? "red"
                                  : "green",
                            }}
                          ></Pencil>
                        </InputGroup.Text>
                      )}
                      {(errors as formErrors)[f.value] &&
                        (errors as formErrors)[f.value].map((e) => (
                          <Form.Control.Feedback key={e} type="invalid">
                            {e}
                          </Form.Control.Feedback>
                        ))}
                    </InputGroup>
                  </div>
                </div>
              </Form.Group>
            ))}
          </Form>
          <svg
            id="testsvg"
            viewBox="0 0 278 30"
            style={{ position: "absolute", zIndex: -1 }}
          >
            <text
              ref={svgText}
              style={{
                whiteSpace: "pre",
                strokeWidth: "0.03px",
                stroke: "black",
                fill: "black",
                fontFamily: "Arial",
                fontSize: type === "standard" ? "6pt" : "4.5pt",
              }}
            ></text>
          </svg>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button
            variant="primary"
            form="standardTextStringForm"
            type="submit"
            disabled={formEntries.some((key) => key.value in errors)}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default GlossaryTextModal;
